import { useEffect, useRef, useState } from "react";
import { audioUrl, videoUrls } from "../../../constants/pegaMasMultimedia";
import { useVideoQuality } from "../../../hooks/useVideoQuality";
import { getImageFromDB, storeImageInDB } from '../../../utils/indexdb.utils';
import {
  generateAllImageDisplayConfigs,
  getRatioBasedHeight,
  getXDrawingPos,
  getYDrawingPos,
  OVERLAY_TEXT_OPACITY,
  RESULT_POSITION_CONFIG,
  TOP_OFFSETS_CONFIG,
} from "../../../utils/resources.utils";
import "./styles.css";

interface ResultAnimationProps {
  numbers: number[];
  teardown: () => void;
  videoSrc?: string | null;
}

export const ResultAnimation: React.FC<ResultAnimationProps> = ({
  numbers,
  teardown,
  videoSrc
}) => {
  let currentResultPos = 0;
  const videoQuality = useVideoQuality();
  const videoElement = useRef<HTMLVideoElement>(null);
  const audioElement = useRef<HTMLAudioElement>(null);
  const canvasElement = useRef<HTMLCanvasElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(false);
  const images = useRef<any[]>([]);

  const lazyLoadImages = async (callback: () => void) => {
    const imageConfigs = generateAllImageDisplayConfigs();
    let loadedImagesCount = 0;
  
    images.current = await Promise.all(
      imageConfigs.map(async (config) => {
        const cachedImage = await getImageFromDB(config.src);
  
        if (cachedImage) {
          const img = new Image();
          img.src = URL.createObjectURL(cachedImage);
  
          img.onload = () => {
            loadedImagesCount++;
            if (loadedImagesCount === imageConfigs.length) {
              callback();
            }
          };
  
          return { ...config, img };
        } else {
          const img = new Image();
          img.src = config.src;
  
          img.onload = async () => {
            loadedImagesCount++;
            if (loadedImagesCount === imageConfigs.length) {
              callback();
            }
  
            const response = await fetch(config.src);
            const blob = await response.blob();
            await storeImageInDB(config.src, blob);
          };
  
          img.onerror = () => {
            console.error(`Error al cargar la imagen: ${config.src}`);
          };
  
          return { ...config, img };
        }
      })
    );
  };

  const updateCanvasSize = () => {
    const canvas = canvasElement.current;
    if (canvas) {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
  };

  useEffect(() => {
    if (videoLoaded) {
      lazyLoadImages(() => {
        drawFrame();
      });
    }
  }, [videoLoaded]);

  useEffect(() => {
    updateCanvasSize();
    window.addEventListener("resize", updateCanvasSize);

    return () => {
      window.removeEventListener("resize", updateCanvasSize);
    };
  }, []);

  const drawTextOnCanvas = (
    canvasContext: CanvasRenderingContext2D,
    canvasWidth: number,
    xPos: number,
    yPos: number,
    fontSize: number,
    text: number | string
  ) => {
    const formattedText =
      typeof text === "number" && text < 10
        ? text.toString().padStart(2, "0")
        : text;
    canvasContext.fillStyle = `rgba(0, 0, 0, ${OVERLAY_TEXT_OPACITY})`;
    canvasContext.font = `bold ${fontSize}vw Arial`;
    canvasContext.fillText(
      `${formattedText}`,
      getXDrawingPos(canvasWidth, xPos),
      getYDrawingPos(canvasWidth, yPos)
    );
  };

  const drawMainNumber = (
    canvasContext: CanvasRenderingContext2D,
    canvasWidth: number,
    videoTime: number
  ) => {
    const { main: topPosition } = TOP_OFFSETS_CONFIG;
    const nextResult = RESULT_POSITION_CONFIG[currentResultPos];
    const { start, end, firstPos } = nextResult;

    if (videoTime > start && videoTime < end) {
      drawTextOnCanvas(
        canvasContext,
        canvasWidth,
        firstPos,
        topPosition,
        11,
        numbers[currentResultPos]
      );
    }

    if (videoTime > end) {
      currentResultPos = Math.min(currentResultPos + 1, numbers.length - 1);
    }
  };

  const drawTopResults = (
    canvasContext: CanvasRenderingContext2D,
    canvasWidth: number,
    videoTime: number
  ) => {
    const { top: topPos } = TOP_OFFSETS_CONFIG;

    RESULT_POSITION_CONFIG.forEach((posConfig: any, iteration: number) => {
      const { start, secondPos } = posConfig;

      if (videoTime > start) {
        drawTextOnCanvas(
          canvasContext,
          canvasWidth,
          secondPos,
          topPos,
          3.4,
          numbers[iteration]
        );
      }
    });
  };

  const drawImagesOnCanvas = (
    canvasContext: CanvasRenderingContext2D,
    videoTime: number
  ) => {
    const canvasWidth = canvasElement.current?.width;
    const canvasHeight = canvasElement.current?.height;

    images.current.forEach(({ img, start, end, x, y }) => {
      if (videoTime > start && videoTime < end) {
        canvasContext.drawImage(img, x, y, canvasWidth!, canvasHeight!);
      }
    });
  };

  const drawFrame = () => {
    const video = videoElement.current;
    const canvas = canvasElement.current;

    if (!video || !canvas) {
      return;
    }

    const isVideoFinished = video.currentTime === video.duration;

    if (isVideoFinished) {
      teardown();
      return;
    }

    const canvasContext = canvas.getContext("2d");
    if (!canvasContext) return;

    const videoHeight = getRatioBasedHeight(canvas.width);
    canvas.height = videoHeight;

    canvasContext.drawImage(
      video,
      0,
      0,
      canvas.width,
      getRatioBasedHeight(canvas.width)
    );

    drawMainNumber(canvasContext, canvas.width, video.currentTime);
    drawTopResults(canvasContext, canvas.width, video.currentTime);
    drawImagesOnCanvas(canvasContext, video.currentTime);

    requestAnimationFrame(drawFrame);
  };

  const syncPlayback = () => {
    const video = videoElement.current;
    const audio = audioElement.current;

    if (video && audio) {
      const diff = Math.abs(video.currentTime - audio.currentTime);
      if (diff > 0.1) {
        console.warn(`Corrigiendo desincronización: ${diff.toFixed(2)}s`);
        audio.currentTime = video.currentTime;
      }
    }
  };

  const handlePlayError = async (element: HTMLMediaElement) => {
    try {
      await element.play();
    } catch (error: any) {
      if (error.name === "NotAllowedError") {
        console.warn("Reproducción bloqueada. Mostrando botón para iniciar manualmente.");
        setShowPlayButton(true);
      } else {
        console.error("Error en la reproducción:", error);
      }
    }
  };

  useEffect(() => {
    if (videoElement.current) {
      videoElement.current.addEventListener("canplaythrough", () => setVideoLoaded(true));
    }
    if (audioElement.current) {
      audioElement.current.addEventListener("canplaythrough", () => setAudioLoaded(true));
    }

    return () => {
      videoElement.current?.removeEventListener("canplaythrough", () => setVideoLoaded(true));
      audioElement.current?.removeEventListener("canplaythrough", () => setAudioLoaded(true));
    };
  }, []);

  useEffect(() => {
    if (videoLoaded && audioLoaded && !isReady) {
      setIsReady(true);
      drawFrame();
      if (videoElement.current) handlePlayError(videoElement.current);
      if (audioElement.current) handlePlayError(audioElement.current);
    }
  }, [videoLoaded, audioLoaded, isReady]);

  useEffect(() => {
    if ("connection" in navigator) {
      const connection = (navigator as any).connection;

      if (connection.downlink < 1.5) {
        videoElement.current!.playbackRate = 0.9;
        audioElement.current!.playbackRate = 0.9;
        console.warn("Conexión lenta detectada, reduciendo la velocidad de reproducción.");
      }
    } else {
      console.warn("Network Information API no soportada en este navegador.");
    }
  }, []);
  
  return (
    <div
      style={{
        position: "relative",
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <video
        ref={videoElement}
        muted
        controls={false}
        crossOrigin="anonymous"
        preload="auto"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: -1,
        }}
        onEnded={teardown}
        onError={(e) => console.error("Error al cargar el video:", e)}
        onTimeUpdate={syncPlayback}
      >
        <source src={videoSrc || videoUrls['low']} type="video/webm" />
      </video>

      <audio ref={audioElement} crossOrigin="anonymous" preload="auto">
        <source src={audioUrl} type="audio/mp3" />
      </audio>

      <canvas
        ref={canvasElement}
        width={window.innerWidth}
        height={window.innerHeight}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 1,
        }}
      />
    </div>
  );
};